import { useI18n } from 'vue-i18n'

import { useDataStore } from '@/stores/data'
import { useExhibitStore } from '@/stores/exhibit'

interface UseServiceWorkerReturnValue {
  /**
   * Attempts to cache media urls.
   * Will bail if either:
   * - Service workers are not available
   * - The service worker is not yet installed
   * - The current locale has already sent a cache request
   */
  cacheMedia(): void
}

export function useServiceWorker (): UseServiceWorkerReturnValue {
  if (!('serviceWorker' in navigator)) {
    return {
      cacheMedia () { },
    }
  }

  const { locale } = useI18n()
  const cachedLocales = new Set<string>()
  const exhibitStore = useExhibitStore()
  const dataStore = useDataStore()
  const cacheMedia: UseServiceWorkerReturnValue['cacheMedia'] = () => {
    // abandon if sw not active, the next route change will cache instead
    if (!navigator.serviceWorker.controller) return

    // only send one cache request per locale
    const loc = locale.value
    if (cachedLocales.has(loc)) return

    cachedLocales.add(loc)
    const exhibits = exhibitStore.exhibits
    const urls = new Set(
      exhibits
        .reduce<string[]>((acc, exhibit) => {
          if (exhibit.hasSyncedPlayback && exhibit.experiences) {
            acc.push(
              ...exhibit.experiences.map(e => dataStore.getMediaSrc(e.media)),
            )
          }
          return acc
        }, [])
        .filter((url) => !!url),
    )

    if (urls.size === 0) return
    navigator.serviceWorker.controller.postMessage({
      type: 'cache-media',
      urls: [...urls],
    })
  }

  return {
    cacheMedia,
  }
}
